/**
 * Shared hook for relay-style cursor pagination with Apollo Client.
 *
 * Eliminates the copy-pasted fetchMore + hasNextPage + endCursor pattern
 * found across 28+ files by encapsulating the guard logic (loading state,
 * cursor presence, hasNextPage check) into a single reusable hook.
 * @module hooks/useInfiniteScroll
 */

import { NetworkStatus } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Relay-style pageInfo shape returned by all paginated GraphQL queries.
 */
interface PageInfo {
  readonly hasNextPage: boolean;
  readonly endCursor?: string | null;
}

/**
 * Configuration for the useInfiniteScroll hook.
 */
interface UseInfiniteScrollOptions {
  /** PageInfo object from the query result (e.g., data?.searchPlayers?.pageInfo) */
  readonly pageInfo: PageInfo | undefined | null;
  /** Apollo fetchMore function from the query hook */
  readonly fetchMore: (options: {
    variables: Record<string, unknown>;
  }) => Promise<unknown>;
  /** Apollo networkStatus from the query hook, used to guard against concurrent fetches */
  readonly networkStatus: NetworkStatus;
  /** Additional variables to include in the fetchMore call alongside the cursor */
  readonly extraVariables?: Record<string, unknown>;
}

/** Distance from bottom (in pixels) at which loadMore is triggered */
const SCROLL_THRESHOLD = 200;

/**
 * Return value from the useInfiniteScroll hook.
 */
interface UseInfiniteScrollReturn {
  /** Triggers the next page fetch if conditions are met (has more data, not already loading) */
  readonly loadMore: () => void;
  /** Whether there are more pages available */
  readonly hasMore: boolean;
  /** Whether a fetchMore request is currently in progress */
  readonly isLoadingMore: boolean;
  /** Ref to attach to a scrollable container for automatic scroll-based pagination */
  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Encapsulates the relay-style pagination pattern used across the app.
 *
 * Guards against concurrent fetches by checking all in-flight networkStatus
 * values (loading, fetchMore, refetch, setVariables) plus a synchronous
 * useRef in-flight flag to prevent re-entrant calls within the same render
 * cycle. Only triggers fetchMore when hasNextPage is true and endCursor is present.
 * @param options - Pagination configuration from the query result
 * @param options.pageInfo - Relay pageInfo object with hasNextPage and endCursor
 * @param options.fetchMore - Apollo fetchMore function from the query hook
 * @param options.networkStatus - Apollo networkStatus to guard against concurrent fetches
 * @param options.extraVariables - Additional variables merged into the fetchMore call
 * @returns Object with loadMore callback, hasMore flag, and isLoadingMore status
 * @example
 * ```typescript
 * const { data, fetchMore, networkStatus } = useMyPaginatedQuery({ variables });
 * const { loadMore, hasMore, isLoadingMore } = useInfiniteScroll({
 *   pageInfo: data?.myQuery?.pageInfo,
 *   fetchMore,
 *   networkStatus,
 *   extraVariables: { query: searchTerm },
 * });
 * ```
 */
export const useInfiniteScroll = ({
  pageInfo,
  fetchMore,
  networkStatus,
  extraVariables,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore = pageInfo?.hasNextPage ?? false;
  const isBusy =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;
  const inFlightRef = useRef(false);

  const loadMore = useCallback(() => {
    const endCursor = pageInfo?.endCursor;
    if (!hasMore || !endCursor || isBusy || inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    void fetchMore({
      variables: { ...extraVariables, after: endCursor },
    })
      .catch((error: unknown) => {
        console.warn("[useInfiniteScroll] loadMore failed", error);
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [pageInfo?.endCursor, hasMore, isBusy, fetchMore, extraVariables]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  /** Trigger loadMore when user scrolls near the bottom of the container */
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      const distanceFromBottom =
        el.scrollHeight - el.clientHeight - el.scrollTop;
      if (distanceFromBottom < SCROLL_THRESHOLD) {
        loadMore();
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadMore]);

  return useMemo(
    () => ({ loadMore, hasMore, isLoadingMore, scrollContainerRef }),
    [loadMore, hasMore, isLoadingMore]
  );
};
